/**
 * CoinBalancePanels component
 * Renders the parent's Otter Coin balance as TWO DISTINCT panels:
 * one for monthly subscription coins and one for top-up/welcome coins.
 * Also renders a dismissible welcome-credit banner for brand-new families.
 *
 * Implements US-006 (T5): CoinBalancePanels + WelcomeCreditBanner
 * Modified  US-007 (T1): monthly sub-label "resets on"; top-up sub-label was
 *   originally a hardcoded permanence claim (retired — see TS-014 below).
 * Modified  TS-014 (T1-T3): top-up sub-label now shows a nearest-expiry
 *   indicator (FR-8.1/8.2, CCM-04); the original permanence claim was retired
 *   because top-up coins now lapse 30 days after grant, so it was factually
 *   wrong.
 * Redesign (2026-06): restyled as warm gradient tiles (Fredoka numerals, otter
 *   palette) to match the parent dashboard redesign. ALL rendered strings and
 *   aria-labels are preserved verbatim ("Monthly Coins" / "Top-up Coins",
 *   "{n} Monthly Coins" aria-label, "resets on …", and the welcome-credit
 *   banner copy) so the existing contract/QA tests stay valid. TS-014 is the
 *   documented exception: the top-up permanence sub-label is replaced by the
 *   nearest-expiry indicator.
 *
 * CCM-02 INVARIANT: monthlyCoins and topupCoins are ALWAYS shown as separate
 * panels. They must NEVER be merged or summed into a single total.
 *
 * CCM-03: This component is rendered only behind ParentGuard.
 * Reusable by the Plan & Coins tab and the Overview wallet panel.
 *
 * Modified TS-017 (T6): added optional `onTopupDetail` prop. When supplied, the
 * "Top-up Coins" tile becomes a keyboard-accessible button that opens the parent-only
 * top-up coin detail screen (/parent/topup-coins). The prop is OPTIONAL and defaults to
 * undefined, so every existing render site (US-007 smoke test, TS-014 tests,
 * PlanAndCoinsTab, WalletPanel without the callback) keeps rendering the tile as a plain
 * non-interactive <div>, unchanged. Mirrors the existing optional-callback pattern used by
 * `onDismissWelcome` / `WalletPanel.onManage`.
 */

import otterCoinSvg from '@/assets/otter-coin.svg';
import { cn } from '@/lib/utils';
import type { OtterCoinBalance } from '@/types/coins';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface CoinBalancePanelsProps {
  /** The parent's two-bucket Otter Coin balance */
  balance: OtterCoinBalance;
  /**
   * When true, renders the welcome-credit banner above the panels.
   * Controlled by the parent page which owns the dismiss state.
   */
  isNewFamily: boolean;
  /** Called when the parent clicks the dismiss button on the welcome banner */
  onDismissWelcome?: () => void;
  /**
   * Configured number of free session credits granted on sign-up
   * (coin_config welcomeCoinAmount, via useParentDashboard). Rendered in the
   * welcome banner copy. Optional: when omitted or undefined, the banner falls
   * back to `balance.topupCoins` — which, for a brand-new family, equals the
   * welcome grant — so the count always matches the Top-up Coins tile.
   */
  welcomeCoinAmount?: number;
  /**
   * TS-017: called when the parent activates the "Top-up Coins" tile (click or
   * keyboard). When omitted, the tile renders as a plain non-interactive <div>
   * (unchanged from pre-TS-017 behavior).
   */
  onTopupDetail?: () => void;
  /** Optional additional class names on the container */
  className?: string;
}

// ─── CoinPanel helper ─────────────────────────────────────────────────────────

interface CoinPanelProps {
  label: string;
  value: number;
  subLabel?: string | null;
  /** Visual tone — 'amber' for the monthly bucket, 'orange' for the top-up bucket. */
  tone: 'amber' | 'orange';
  /**
   * TS-017 (T6): when provided, the tile renders as a keyboard-accessible button
   * that fires this callback on click / Enter / Space. Omitted -> plain <div>
   * (pre-TS-017 behavior, exercised by every existing CoinPanel render site).
   */
  onClick?: () => void;
}

const TONE = {
  amber: {
    tile: 'from-amber-50 to-amber-100 border-amber-200',
    num: 'text-amber-600',
  },
  orange: {
    tile: 'from-orange-50 to-orange-100 border-orange-200',
    num: 'text-orange-600',
  },
} as const;

/**
 * Single coin bucket tile — otter-coin icon, the numeric balance, and an
 * optional sub-label. Renders a defensive 0 when value is undefined/null.
 * aria-label preserved as "{value} {label}" for screen readers and tests.
 */
function CoinPanel({ label, value, subLabel, tone, onClick }: CoinPanelProps) {
  const safeValue = value ?? 0;
  const t = TONE[tone];

  const tileContent = (
    <div className="flex flex-col items-center gap-1">
      <img
        src={otterCoinSvg}
        alt="Otter Coin"
        className="h-8 w-8 drop-shadow-sm"
        aria-hidden="true"
      />
      <span
        className={cn('font-display text-3xl font-bold leading-none', t.num)}
        aria-label={`${safeValue} ${label}`}
      >
        {safeValue}
      </span>
      {/* US-007 AC-3: label stays a neutral gray — gold/amber is scoped to icon + count only */}
      <span className="text-sm font-semibold text-gray-700">{label}</span>
      {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
    </div>
  );

  // TS-017 (T6): keyboard-accessible button variant, only when a callback is
  // supplied. Preserves the existing tile classes/tones and the aria-label on
  // the value span (US-007/TS-014 tests assert it — never removed).
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`View ${label} details`}
        className={cn(
          'flex-1 min-w-0 rounded-2xl border bg-gradient-to-br p-4 text-center',
          'min-h-[44px] cursor-pointer transition-transform hover:scale-[1.02]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400',
          t.tile
        )}
      >
        {tileContent}
      </button>
    );
  }

  return (
    <div
      className={cn(
        'flex-1 min-w-0 rounded-2xl border bg-gradient-to-br p-4 text-center',
        t.tile
      )}
    >
      {tileContent}
    </div>
  );
}

// ─── WelcomeCreditBanner ──────────────────────────────────────────────────────

interface WelcomeCreditBannerProps {
  /**
   * Number of free session credits granted on sign-up — rendered in the copy so
   * the banner always matches the actual grant (coin_config welcomeCoinAmount).
   * Sourced from useParentDashboard().welcomeCoinAmount, with the family's top-up
   * balance as a fallback (see CoinBalancePanels). No longer a hardcoded "1".
   */
  amount: number;
  onDismiss?: () => void;
}

/**
 * Dismissible welcome banner for new families who have received the free welcome
 * coins. The credit count is data-driven (`amount`) so the copy always reflects
 * the configured welcomeCoinAmount (e.g. 1 -> 3) with correct pluralization.
 * Exported so the Overview tab can render it above the hero row.
 */
export function WelcomeCreditBanner({ amount, onDismiss }: WelcomeCreditBannerProps) {
  // Defensive floor of 1: the banner only renders for families that hold the
  // welcome credit, so a non-positive amount should never occur — but never show
  // "0 free session credits" if an upstream value is missing.
  const credits = amount >= 1 ? amount : 1;
  const isSingular = credits === 1;

  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <img
            src={otterCoinSvg}
            alt=""
            className="mt-0.5 h-8 w-8 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="mb-1 text-sm font-semibold text-amber-800">
              Welcome to ThinkOtter!
            </p>
            <p className="text-sm text-amber-700">
              {/* AC-1: credit count reads live from welcomeCoinAmount and is reflected in the Top-up Coins panel */}
              We&apos;ve added {credits} free session credit{isSingular ? '' : 's'} to
              your Top-up Coins so your child can{' '}
              {isSingular ? 'try a tutoring session' : 'try tutoring sessions'}. Start
              exploring anytime!
            </p>
          </div>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            aria-label="Dismiss welcome message"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ─── CoinBalancePanels (main export) ─────────────────────────────────────────

/**
 * CoinBalancePanels — renders the parent's wallet as two distinct coin tiles.
 * CCM-02: monthlyCoins and topupCoins are always separate — never summed.
 */
export function CoinBalancePanels({
  balance,
  isNewFamily,
  onDismissWelcome,
  onTopupDetail,
  welcomeCoinAmount,
  className,
}: CoinBalancePanelsProps) {
  // Welcome-banner credit count: prefer the configured welcomeCoinAmount; fall
  // back to the top-up balance (equals the welcome grant for a brand-new family),
  // so the banner never shows a stale hardcoded number and always matches the tile.
  const welcomeCredits = welcomeCoinAmount ?? balance.topupCoins ?? 1;
  // Format the reset date human-readably; omit entirely when null (CCM defensive).
  // US-007 (T1): prefix "resets on"; guards Invalid Date (NaN timestamp).
  // BUG-20260703-130127 (2026-07-03): reads balance.nextBillingDate (the real
  // Stripe current_period_end) instead of monthlyCoinsResetDate (the coin-grant
  // timestamp). Wording/guard/formatting unchanged — only the source field.
  // TO-LL-044: reads balance.nextBillingDate — exact camelCase key.
  const resetDateLabel: string | null = balance.nextBillingDate
    ? (() => {
        try {
          const date = new Date(balance.nextBillingDate);
          if (isNaN(date.getTime())) return null;
          return `resets on ${date.toLocaleDateString('en-SG', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}`;
        } catch {
          return null;
        }
      })()
    : null;

  // Format the nearest top-up expiry as a human-readable sub-label; null when
  // there is nothing expiring (empty state — CoinPanel's `{subLabel && ...}`
  // guard then renders no sub-label at all).
  // Implements TS-014 (T2, AC-TS014.1/.2, BR-TS014.1): retires the hardcoded
  // permanence sub-label now that top-up coins lapse 30 days after grant.
  // TO-LL-044: reads balance.topupNearestExpiryDate / topupNearestExpiryAmount
  // — exact camelCase keys (TS-013 contract). Mirrors the resetDateLabel IIFE
  // shape above, but pins timeZone: 'Asia/Singapore' (required — the field is
  // a date-only string and would otherwise day-shift in non-SGT runtimes).
  const topupExpiryLabel: string | null =
    typeof balance.topupNearestExpiryAmount === 'number' &&
    balance.topupNearestExpiryAmount > 0 &&
    balance.topupNearestExpiryDate
      ? (() => {
          try {
            const date = new Date(balance.topupNearestExpiryDate as string);
            if (isNaN(date.getTime())) return null;
            const amount = balance.topupNearestExpiryAmount as number;
            const formatted = date.toLocaleDateString('en-SG', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              timeZone: 'Asia/Singapore',
            });
            return `${amount} ${amount === 1 ? 'coin' : 'coins'} expire${
              amount === 1 ? 's' : ''
            } on ${formatted}`;
          } catch {
            return null;
          }
        })()
      : null;

  return (
    <div className={cn('w-full', className)}>
      {/* Welcome banner — shown only for brand-new families (AC-1, AC-3) */}
      {isNewFamily && (
        <WelcomeCreditBanner amount={welcomeCredits} onDismiss={onDismissWelcome} />
      )}

      {/* Two DISTINCT coin tiles — CCM-02: never merged into one total. */}
      <div className="flex flex-row gap-3">
        {/* Monthly subscription bucket */}
        <CoinPanel
          label="Monthly Coins"
          value={balance.monthlyCoins}
          subLabel={resetDateLabel}
          tone="amber"
        />

        {/* Top-up / welcome credit bucket — nearest-expiry indicator (TS-014, retires the old permanence sub-label).
            TS-017 (T6, AC-TS017.1): only this tile becomes clickable when onTopupDetail is
            supplied; the Monthly tile stays non-interactive. */}
        <CoinPanel
          label="Top-up Coins"
          value={balance.topupCoins}
          subLabel={topupExpiryLabel}
          tone="orange"
          onClick={onTopupDetail}
        />
      </div>
    </div>
  );
}
